import { AnimatedVerticalLogo } from '@/assets/animated-vertical-logo';
import { MobileDownloadBanner } from '@/components/common/mobile-download-banner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SUPPORTED_LOCALES } from '@/i18n/i18n';
import { useAuthStore } from '@/stores/auth';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Login } from './login';
import { OTPPage } from './otp';
import { AppleLogin } from './social-login/apple';
import { GoogleLogin } from './social-login/google';

export const NATIVE_SOCIAL_LOGIN_REDIRECT_TO =
  'voicefill-medical://auth-callback/';

export const AuthPage = () => {
  const { i18n, t } = useTranslation();

  const currentLanguage = i18n.language.split('-')[0] || 'en';

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
  };

  // Use Zustand store for auth state
  const { step, email, setStep, setEmail, clearAuth } = useAuthStore();
  const isFirstRender = useRef(true);
  const prevStepRef = useRef(step);

  // Clear auth state when transitioning from 'otp' to 'login' (but not on initial mount)
  useEffect(() => {
    if (
      !isFirstRender.current &&
      prevStepRef.current === 'otp' &&
      step === 'login'
    ) {
      clearAuth();
    }
    prevStepRef.current = step;
  }, [step, clearAuth]);

  // Track when step changes to detect transitions
  useEffect(() => {
    // Once step changes from initial 'login', mark as not first render
    if (step !== 'login' || !isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [step]);

  // Animation variants for initial fade-up effect (only on first load)
  const initialContainerVariants = {
    hidden: {},
    visible: {
      transition: {
        delayChildren: 0.8, // Start after logo animation completes
        staggerChildren: 0.05,
      },
    },
  };

  // Animation variants for transitions (no delay)
  const transitionContainerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as const,
      },
    },
  };

  // Animation variants for screen transitions
  const screenTransitionVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: 'easeOut' as const,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <>
      <MobileDownloadBanner />
      <div className="flex h-full w-full flex-col items-center justify-between gap-6 px-8 md:justify-end md:gap-16">
        <div className="flex min-h-fit w-full flex-1 grow items-end justify-center py-2 md:flex-7">
          <AnimatedVerticalLogo size={156} animate={step === 'login'} />
        </div>
        <div className="flex w-full max-w-sm flex-1 flex-col items-center justify-end gap-3 md:flex-5 md:pb-12">
          <AnimatePresence mode="wait">
            {step === 'login' ? (
              <motion.div
                key="login"
                className="flex w-full flex-col items-center justify-center gap-8"
                variants={screenTransitionVariants}
                initial="initial"
                animate="animate"
                exit="exit">
                <motion.div
                  variants={
                    isFirstRender.current
                      ? initialContainerVariants
                      : transitionContainerVariants
                  }
                  initial="hidden"
                  animate="visible"
                  className="flex w-full flex-col items-center justify-center gap-6">
                  <Login
                    onContinue={(userEmail) => {
                      setEmail(userEmail);
                      setStep('otp');
                    }}
                  />
                  <motion.div variants={itemVariants} className="w-full">
                    <div className="bg-border h-px w-full" />
                  </motion.div>
                  <div className="flex w-full flex-col items-center justify-center gap-2">
                    <motion.div variants={itemVariants} className="w-full">
                      <AppleLogin />
                    </motion.div>
                    <motion.div variants={itemVariants} className="w-full">
                      <GoogleLogin />
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                key="otp"
                className="flex w-full flex-col items-center justify-center gap-8"
                variants={screenTransitionVariants}
                initial="initial"
                animate="animate"
                exit="exit">
                <motion.div
                  variants={transitionContainerVariants}
                  initial="hidden"
                  animate="visible"
                  className="flex w-full flex-col items-center justify-center gap-8">
                  <OTPPage email={email} onBack={() => setStep('login')} />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          <motion.div
            className="flex w-full flex-col items-center justify-end"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{
              delay: 3.0, // Start after form elements finish animating
            }}>
            <Select
              value={currentLanguage}
              onValueChange={handleLanguageChange}>
              <SelectTrigger className="text-muted-foreground hover:bg-accent w-auto gap-1.5 border-0 bg-transparent shadow-none">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent align="center">
                {SUPPORTED_LOCALES.map((lang) => (
                  <SelectItem key={lang} value={lang}>
                    {t(`home:languages.${lang}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </motion.div>
        </div>
      </div>
    </>
  );
};
